import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Alert, Button, Card, Search, Checkbox, FilterChip, Input, ItemList, ItemListRow, Segmented, Skeleton, StatusHint, Tooltip,
} from '@indipia/ui-core';
import { useAuthStore } from '@indipia/shared';
import { stepBSchema, type StepBFormValues } from './formSchemas';
import { AssigneeSelect, BellLockIcon, useCompanyMembers, useUserDefaultedState } from '../site-assign';
import { searchAddress, fetchCoord, fetchBuildings, createBuilding } from './api';
import type { AddressSearchItem, CreateSitePayload, StepAResult } from './types';

interface StepBProps {
  stepAResult: StepAResult;
  isPyeongRequired: boolean;
  /** 현장을 생성하는 에이전트 ID (활동로그 귀속용). 플랫폼 직접 등록=0 */
  agentId: number;
  /**
   * 현장 입력 완료 → createSite payload 전달 (실 저장은 팝업이 담당).
   * gpsLat/gpsLng는 도로명 주소 자동 지오코딩 좌표. mapStep=true면 지도 확정 좌표로 덮어쓴다.
   */
  onSubmit: (payload: CreateSitePayload) => void;
  onPrev: () => void;
  /** 저장/지도 단계 진행 중 — 버튼 비활성 */
  submitting?: boolean;
  /** '다음' 버튼 문구 (mapStep이면 '지도에서 위치 지정 →') */
  nextLabel?: string;
  /** 초기값 — 현장명·평형을 미리 채우고, 현장주소는 검색창 초기 검색어로 쓴다 */
  prefill?: { siteName?: string; siteAddress?: string; sitePyeong?: number | null };
}

/** 동 · 호 입력값 끝의 단위 제거 — '101동' → '101' (화면이 표시할 때 단위를 붙인다) */
function stripUnit(value: string | undefined, unit: '동' | '호'): string {
  const v = (value ?? '').trim();
  return v.endsWith(unit) ? v.slice(0, -unit.length).trim() : v;
}

/** "32평(105㎡)" | "32평" | "105㎡" | null 파싱 */
function parseSizeLabel(label: string | null): { pyeong: number | null; squareMeters: number | null } {
  if (!label) return { pyeong: null, squareMeters: null };
  const pyeongMatch = label.match(/(\d+)\s*평/);
  const sqMatch = label.match(/(\d+)\s*㎡/);
  return {
    pyeong: pyeongMatch ? parseInt(pyeongMatch[1], 10) : null,
    squareMeters: sqMatch ? parseInt(sqMatch[1], 10) : null,
  };
}

const SQ_PER_PYEONG = 3.3058;

/**
 * Step B — 현장 정보 입력 (기획서 E-4 기준)
 *
 * 필드:
 *   - "고객 주소와 동일" 체크박스
 *   - 현장 주소 * (검색 → 선택 시 확인 완료 표시)
 *   - 상세주소, 단지명, 동, 호수, 평수, 현장명, 메모
 *
 * 평수 처리:
 *   - 단지 마스터 있음: <select> → sizeLabel 파싱 → pyeong + squareMeters 추출
 *   - 단지 마스터 없음: 직접 입력(평) → squareMeters 자동 계산 (×3.3058)
 *
 * 완료 후: createSite payload를 팝업으로 전달 (저장·좌표 확정은 팝업이 담당)
 */
export function StepB({ stepAResult, isPyeongRequired, agentId, onSubmit, onPrev, submitting = false, nextLabel = '다음 →', prefill }: StepBProps) {
  // Step A에서 주소를 선택했으면 그대로 사용 (재검색 불필요)
  const prefilledItem = stepAResult.addressItem ?? null;
  const [sameAsCustomer, setSameAsCustomer] = useState(!!stepAResult.customerAddress);
  // 고객주소를 그대로 쓰지 않을 때는 추출 현장주소를 검색창 초기값으로 둬 바로 검색되게 한다
  const [searchQuery, setSearchQuery] = useState(
    !stepAResult.customerAddress && prefill?.siteAddress ? prefill.siteAddress : '',
  );
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedAddress, setSelectedAddress] = useState<AddressSearchItem | null>(
    sameAsCustomer ? prefilledItem : null,
  );
  const [confirmedAddress, setConfirmedAddress] = useState<string>(
    stepAResult.customerAddress ?? '',
  );
  const [gpsLat, setGpsLat] = useState<number | undefined>();
  const [gpsLng, setGpsLng] = useState<number | undefined>();
  const [autoMatchName, setAutoMatchName] = useState<string>(
    sameAsCustomer && prefilledItem ? (prefilledItem.buildingName ?? '') : '',
  );

  // 현장 담당 지정(2026-10-12 개선사항 D-1 · C-21) — 기본값: 등록자 본인이 현장 담당자, 관리자는 직접 선택
  const authUser = useAuthStore((s) => s.user);
  const myId = authUser?.memberId ?? null;
  const { data: companyMembers = [] } = useCompanyMembers();
  // 로그인 정보가 늦게 들어와도 본인이 기본 담당자가 되도록 사용자 기준으로 기본값을 다시 맞춘다
  const [chargeIds, setChargeIds] = useUserDefaultedState<number[]>(String(myId ?? ''), myId != null ? [myId] : []);
  const [managerIds, setManagerIds] = useState<number[]>([]);
  // 현장 관리자 알림 받기(20261015 E-2) — 지정한 관리자 전원에 일괄 적용. 등록 후 현장상세에서 사람별로 바꾼다.
  // 관리자를 지정하면 알림 수신 여부는 필수(확정 결정 6) — 선택 전('')에는 어느 쪽도 켜지지 않는다
  const [managerNoti, setManagerNoti] = useState<'on' | 'off' | ''>('');
  const chargeEmpty = chargeIds.length === 0;
  // 관리자 자체는 필수가 아니다 — 관리자가 있을 때만 수신 여부 선택을 요구한다
  const managerNotiEmpty = managerIds.length > 0 && managerNoti === '';
  const myNames = myId != null ? { [myId]: authUser?.memberName ?? null } : undefined;

  // 건물 선택 결과 (hasDanji 경로)
  const [selectedBldgId, setSelectedBldgId] = useState<number | undefined>();
  const [selectedBldgPyeong, setSelectedBldgPyeong] = useState<number | null>(null);
  const [selectedBldgSqm, setSelectedBldgSqm] = useState<number | null>(null);
  const [pyeongDirectInput, setPyeongDirectInput] = useState(false);
  const [isPyeong, setIsPyeong] = useState(true); // 직접 입력 단위: true=평, false=㎡
  const [pyeongError, setPyeongError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<StepBFormValues>({
    resolver: zodResolver(stepBSchema),
    mode: 'onBlur',
    defaultValues: {
      siteAddress: confirmedAddress,
      addressDtl: '',
      bldgName: (sameAsCustomer && prefilledItem?.buildingName) ? prefilledItem.buildingName : '',
      sitePyeong: prefill?.sitePyeong != null ? String(prefill.sitePyeong) : '',
      addressDong: '',
      addressHo: '',
      siteName: prefill?.siteName ?? '',
      siteMemo: '',
    },
  });

  const pyeongValue = watch('sitePyeong') ?? '';
  const bldgName = watch('bldgName') ?? '';
  const dong = watch('addressDong') ?? '';
  const ho = watch('addressHo') ?? '';

  // Step A에서 받은 주소로 마운트 시 좌표 자동 조회
  useEffect(() => {
    if (!prefilledItem || !sameAsCustomer) return;
    fetchCoord({
      admCd: prefilledItem.admCd, rnMgtSn: prefilledItem.rnMgtSn,
      udrtYn: prefilledItem.udrtYn, buldMnnm: prefilledItem.buldMnnm, buldSlno: prefilledItem.buldSlno,
    }).then((coord) => { setGpsLat(coord.latitude); setGpsLng(coord.longitude); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // bldgName/dong/ho 변경 시 현장명 자동 세팅
  useEffect(() => {
    const parts: string[] = [];
    if (bldgName) parts.push(bldgName);
    // 사용자가 '101동'처럼 단위까지 적어도 '101동동'이 되지 않게 단위를 떼고 붙인다 (저장값도 서버가 단위 없이 정리)
    const dongNo = stripUnit(dong, '동');
    const hoNo = stripUnit(ho, '호');
    if (dongNo) parts.push(`${dongNo}동`);
    if (hoNo) parts.push(`${hoNo}호`);
    if (parts.length > 0) {
      setValue('siteName', parts.join(' '), { shouldValidate: true });
    }
  }, [bldgName, dong, ho]);

  // 300ms 디바운스
  useEffect(() => {
    if (sameAsCustomer) return;
    const id = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(id);
  }, [searchQuery, sameAsCustomer]);

  const { data: addressResults = [], isFetching: isSearching, isError: isAddrError, error: addrError } = useQuery({
    queryKey: ['address-search', debouncedQuery],
    queryFn: () => searchAddress(debouncedQuery),
    enabled: !sameAsCustomer && debouncedQuery.trim().length >= 2 && !selectedAddress,
    staleTime: 30_000,
  });

  const { data: buildings = [] } = useQuery({
    queryKey: ['buildings', selectedAddress?.roadAddress],
    queryFn: () => fetchBuildings(selectedAddress!.roadAddress),
    enabled: !!selectedAddress,
    staleTime: 5 * 60_000,
  });

  const [buildingBusy, setBuildingBusy] = useState(false);

  function handleSelectAddress(item: AddressSearchItem) {
    setSelectedAddress(item);
    setSearchQuery('');
    setDebouncedQuery('');
    setConfirmedAddress(item.roadAddress);
    setValue('siteAddress', item.roadAddress, { shouldValidate: true });
    setAutoMatchName(item.buildingName || '');
    if (item.buildingName) {
      setValue('bldgName', item.buildingName, { shouldValidate: true });
    }
    // 건물 선택 초기화
    setSelectedBldgId(undefined);
    setSelectedBldgPyeong(null);
    setSelectedBldgSqm(null);
    setValue('sitePyeong', '');

    // 좌표 변환 (non-blocking)
    fetchCoord({
      admCd: item.admCd, rnMgtSn: item.rnMgtSn, udrtYn: item.udrtYn,
      buldMnnm: item.buldMnnm, buldSlno: item.buldSlno,
    }).then((coord) => { setGpsLat(coord.latitude); setGpsLng(coord.longitude); })
      .catch(() => {});
  }

  function handleSameToggle(checked: boolean) {
    setSameAsCustomer(checked);
    if (checked && stepAResult.customerAddress) {
      setConfirmedAddress(stepAResult.customerAddress);
      setValue('siteAddress', stepAResult.customerAddress, { shouldValidate: true });
      // Step A addressItem이 있으면 그대로 재사용 (좌표·건물 쿼리 활성화)
      setSelectedAddress(prefilledItem);
      setAutoMatchName(prefilledItem?.buildingName ?? '');
      if (prefilledItem?.buildingName) {
        setValue('bldgName', prefilledItem.buildingName, { shouldValidate: true });
      }
      setSearchQuery('');
      setDebouncedQuery('');
    } else {
      setConfirmedAddress('');
      setValue('siteAddress', '');
      setValue('bldgName', '');
      setSelectedAddress(null);
      setAutoMatchName('');
    }
  }

  const handleFormSubmit = async (values: StepBFormValues) => {
    if (chargeEmpty) return;   // 현장 담당자 1명 이상 필수(D-1) — 버튼도 비활성
    if (managerNotiEmpty) return;   // 관리자 지정 시 알림 수신 여부 필수 — 버튼도 비활성
    if (isPyeongRequired && !values.sitePyeong?.trim() && selectedBldgId === undefined) {
      setPyeongError('평수 정보는 필수입니다.');
      return;
    }

    let finalPyeong: number | undefined;
    let finalSqm: number | undefined;

    if (hasDanji && !pyeongDirectInput && selectedBldgId !== undefined) {
      // 칩 선택 경로 — 기존 단지 마스터 bldgId 그대로 사용
      finalPyeong = selectedBldgPyeong ?? undefined;
      finalSqm = selectedBldgSqm ?? undefined;
    } else if (values.sitePyeong) {
      // 직접 입력 경로: 단위에 따라 양방향 변환
      const v = parseInt(values.sitePyeong, 10);
      if (!isNaN(v) && v > 0) {
        if (isPyeong) {
          finalPyeong = v;
          finalSqm = Math.round(v * SQ_PER_PYEONG);
        } else {
          finalSqm = v;
          finalPyeong = Math.round(v / SQ_PER_PYEONG);
        }
      }
    }

    // 직접 입력 시 신규 단지 마스터 생성 (모바일 동일 로직)
    // bldgId 없고 평/면적 값이 있을 때만 POST /indipia/buildings
    let resolvedBldgId = selectedBldgId;
    if (resolvedBldgId === undefined && (finalPyeong !== undefined || finalSqm !== undefined)) {
      try {
        setBuildingBusy(true);
        const { bldgId } = await createBuilding({
          bldgName: values.bldgName || undefined,
          roadAddress: values.siteAddress,
          bldgPyeong: finalPyeong,
          squareMeters: finalSqm,
        });
        resolvedBldgId = bldgId;
      } catch {
        toast.error('단지 마스터 저장에 실패했습니다.');
        return;
      } finally {
        setBuildingBusy(false);
      }
    }

    // createSite payload 조립 → 팝업으로 전달 (mapStep이면 지도 확정 좌표로 gps 덮어씀)
    onSubmit({
      agentId,
      customerId: stepAResult.customerId,
      siteName: values.siteName,
      siteAddress: values.siteAddress,
      addressDtl: values.addressDtl || undefined,
      gpsLat,
      gpsLng,
      bldgId: resolvedBldgId,
      bldgName: values.bldgName || undefined,
      addressDong: stripUnit(values.addressDong, '동') || undefined,
      addressHo: stripUnit(values.addressHo, '호') || undefined,
      // 시군구코드 = 현장 주소 admCd 앞5(고객주소 동일이면 prefill된 고객주소에서, 아니면 재검색분)
      sigunguCode: selectedAddress?.admCd ? selectedAddress.admCd.slice(0, 5) : undefined,
      sitePyeong: finalPyeong,
      squareMeters: finalSqm,
      siteMemo: values.siteMemo || undefined,
      chargeMemberIds: chargeIds,
      managerMemberIds: managerIds,
      // 켬 = 관리자 전원 'Y', 끔 = 전원 'N'(담당자는 서버가 항상 'Y')
      managerNotiMemberIds: managerNoti === 'on' ? managerIds : [],
    });
  };

  const hasDanji = buildings.length > 0;

  return (
    <div className="space-y-4">

      {/* 고객 주소와 동일 체크박스 */}
      {stepAResult.customerAddress && (
        <Checkbox
          checked={sameAsCustomer}
          onChange={(e) => handleSameToggle(e.target.checked)}
          label={`고객 주소와 동일 (${stepAResult.customerAddress})`}
          className="rounded-xxl bg-surface-subtle px-3 py-2.5 text-caption text-text-secondary"
        />
      )}

      {/* 현장 주소 — Search 는 label prop 이 없어 .field 구조를 직접 만든다(라벨 타이포·.req·.help 가 .field 하위 규칙) */}
      <div className={`field field-auto${errors.siteAddress && !confirmedAddress ? ' error' : ''}`}>
        <label htmlFor="stepB-address">
          현장 주소 <span className="req" aria-hidden="true">*</span>
        </label>

        {confirmedAddress ? (
          <div className="flex items-center gap-2">
            {/* 확정된 주소는 표시 전용 readOnly Input — 폼 값은 아래 hidden input 이 담당한다 */}
            <Input id="stepB-address" value={confirmedAddress} readOnly width="fill" />
            {!sameAsCustomer && (
              <Button
                variant="etc"
                size="md"
                type="button"
                onClick={() => {
                  setConfirmedAddress('');
                  setValue('siteAddress', '');
                  setSelectedAddress(null);
                  setAutoMatchName('');
                  setSelectedBldgId(undefined);
                  setSelectedBldgPyeong(null);
                  setSelectedBldgSqm(null);
                }}
              >
                변경
              </Button>
            )}
          </div>
        ) : (
          <div className="relative">
            <Search
              id="stepB-address"
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="도로명 주소를 검색하세요"
            />
            {isAddrError && (
              <Alert kind="error" className="mt-1.5">
                {(addrError as { response?: { data?: { message?: string } } } | null)?.response?.data?.message ?? '주소 검색에 실패했습니다. 다시 시도해주세요.'}
              </Alert>
            )}
            {(addressResults.length > 0 || isSearching) && (
              <div className="absolute top-full z-dropdown mt-1 max-h-44 w-full overflow-y-auto rounded-xxl shadow-[shadow:var(--elevation-2)]">
                {isSearching && (
                  <div className="rounded-xxl border border-border-default bg-surface-raised p-3">
                    <Skeleton variant="text" width="60%" />
                  </div>
                )}
                {addressResults.length > 0 && (
                  <ItemList>
                    {addressResults.map((item, i) => (
                      <ItemListRow
                        key={i}
                        onClick={() => handleSelectAddress(item)}
                        title={item.roadAddress}
                        hint={item.buildingName ? <StatusHint tone="muted">{item.buildingName}</StatusHint> : undefined}
                      />
                    ))}
                  </ItemList>
                )}
              </div>
            )}
          </div>
        )}
        <input type="hidden" {...register('siteAddress')} value={confirmedAddress} />
        {errors.siteAddress && !confirmedAddress && (
          <span className="help">{errors.siteAddress.message}</span>
        )}
      </div>

      {/* 단지 자동 매칭 안내 — 건물 마스터가 실제로 있을 때만 표시 */}
      {hasDanji && autoMatchName && (
        <StatusHint tone="success">
          단지명 자동 매칭: {autoMatchName} · 도면 자동 불러오기 완료
        </StatusHint>
      )}

      {/* 상세주소 */}
      <Input
        id="stepB-addressDtl"
        label="상세주소 (선택)"
        width="auto"
        type="text"
        placeholder="건물명, 층 등"
        {...register('addressDtl')}
      />

      {/* 단지명 */}
      <Input
        id="stepB-bldgName"
        label="단지명 (선택)"
        width="auto"
        type="text"
        placeholder="단지명을 입력하세요"
        {...register('bldgName')}
      />

      {/* 동 / 호수 */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          id="stepB-dong"
          label="동 (선택)"
          width="auto"
          type="text"
          placeholder="예) 101"
          {...register('addressDong')}
        />
        <Input
          id="stepB-ho"
          label="호수 (선택)"
          width="auto"
          type="text"
          placeholder="예) 1201"
          {...register('addressHo')}
        />
      </div>

      {/* 평수 — 칩 선택 / 직접 입력 두 모드라 라벨을 Input 밖에 두고 .field 구조를 직접 만든다 */}
      {/* 칩 모드일 때만 바깥 .field 가 error 를 갖는다 — 직접 입력 모드의 오류는 안쪽 Input 이 그린다 */}
      <div className={`field field-auto${pyeongError && hasDanji && !pyeongDirectInput ? ' error' : ''}`}>
        <label htmlFor="stepB-pyeong">
          평형 정보{' '}
          {isPyeongRequired
            ? <span className="req" aria-hidden="true">*</span>
            : <span className="opt">(선택)</span>
          }
        </label>

        {hasDanji && !pyeongDirectInput ? (
          /* ── 칩 선택 모드 ── */
          <div className={`flex flex-wrap gap-2 rounded-xxl p-1 ${isPyeongRequired && pyeongError ? 'outline outline-1 outline-danger' : ''}`}>
            {buildings.map((b) => {
              const isSelected = selectedBldgId === b.bldgId;
              return (
                <FilterChip
                  key={b.bldgId}
                  size="lg"
                  active={isSelected}
                  onClick={() => {
                    const { pyeong, squareMeters } = parseSizeLabel(b.sizeLabel);
                    setSelectedBldgId(b.bldgId);
                    setSelectedBldgPyeong(pyeong);
                    setSelectedBldgSqm(squareMeters);
                    setValue('sitePyeong', String(b.bldgId));
                    setPyeongError('');
                  }}
                >
                  {b.sizeLabel ?? '평형 미상'}
                </FilterChip>
              );
            })}
            <FilterChip
              size="lg"
              onClick={() => {
                setPyeongDirectInput(true);
                setSelectedBldgId(undefined);
                setSelectedBldgPyeong(null);
                setSelectedBldgSqm(null);
                setValue('sitePyeong', '');
              }}
            >
              직접 입력
            </FilterChip>
          </div>
        ) : (
          /* ── 직접 입력 모드 ── */
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <Input
                id="stepB-pyeong"
                width="fill"
                type="number"
                placeholder="숫자 입력"
                error={pyeongError || undefined}
                {...register('sitePyeong')}
                onChange={(e) => {
                  void register('sitePyeong').onChange(e);
                  if (e.target.value) setPyeongError('');
                }}
              />
              {/* 평 / ㎡ 토글 */}
              <Segmented
                options={[{ label: '평', value: 'pyeong' }, { label: '㎡', value: 'sqm' }]}
                value={isPyeong ? 'pyeong' : 'sqm'}
                onChange={(v) => setIsPyeong(v === 'pyeong')}
              />
            </div>
            {/* 자동 변환 표시 */}
            {pyeongValue && !isNaN(parseInt(pyeongValue, 10)) && parseInt(pyeongValue, 10) > 0 && (
              <StatusHint tone="muted">
                {isPyeong
                  ? `≈ ${Math.round(parseInt(pyeongValue, 10) * SQ_PER_PYEONG)}㎡`
                  : `≈ ${Math.round(parseInt(pyeongValue, 10) / SQ_PER_PYEONG)}평`}
              </StatusHint>
            )}
            {hasDanji && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => { setPyeongDirectInput(false); setValue('sitePyeong', ''); }}
              >
                ← 목록에서 선택
              </Button>
            )}
          </div>
        )}

        {/* 칩 선택 모드에서는 Input 이 없어 오류 문구를 여기서 낸다 */}
        {pyeongError && hasDanji && !pyeongDirectInput && (
          <span className="help">{pyeongError}</span>
        )}
      </div>

      {/* 현장명 */}
      <Input
        id="stepB-siteName"
        label="현장명"
        required
        width="auto"
        type="text"
        placeholder="현장명을 입력해주세요"
        error={errors.siteName?.message}
        {...register('siteName')}
      />

      {/* 현장 담당 지정 — 2026-10-12 개선사항 D-1(C-21), 추출기획서 E-2 '현장 담당 지정' 박스.
          현장 담당자 1명 이상 필수 · 현장 관리자 선택(지정하면 알림 받기 켬/끔 필수). 지정 여부로 조회·수정 권한을 막지 않는다(이번 버전). */}
      {/* .app-card 는 overflow:hidden 이라 안쪽 드롭다운 목록(absolute)이 카드 경계에서 잘린다 — 이 카드만 넘침 허용 */}
      <Card role="group" aria-labelledby="stepB-assign-title" className="!overflow-visible">
        <div className="flex flex-col gap-04">
        <h3 id="stepB-assign-title" className="flex items-center gap-03 text-body-sm font-bold text-text">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-secondary" aria-hidden="true">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          현장 담당 지정
        </h3>
        <Alert kind="info">담당자·관리자로 지정되면 해당 현장이 현장목록에 먼저 보여요.</Alert>
        <div className="field field-fill">
          <label htmlFor="stepB-charge">현장 담당자<span className="req" aria-hidden="true">*</span></label>
          <div className="flex items-start gap-03">
            <AssigneeSelect
              id="stepB-charge"
              className="min-w-0 flex-1"
              members={companyMembers}
              values={chargeIds}
              onChange={setChargeIds}
              myId={myId}
              meFirst
              oppositeIds={managerIds}
              oppositeLabel="관리자"
              extraNames={myNames}
              error={chargeEmpty ? '현장 담당자를 1명 이상 선택해주세요.' : undefined}
            />
            {/* 담당자는 알림을 항상 받음 — 조작 컨트롤 없이 안내만(비활성 컨트롤로 두지 않음, 20261015 E-2) */}
            <Tooltip tip="현장 담당자는 이 현장의 업무 알림을 항상 받아요. 끌 수 없어요." className="shrink-0">
              <span className="flex h-[var(--control-height-base)] items-center gap-02 rounded-md border border-border bg-surface px-04 text-body-sm text-text-secondary">
                <BellLockIcon className="text-text-muted" />
                알림 항상 받음
              </span>
            </Tooltip>
          </div>
        </div>
        <div className={`field field-fill${managerNotiEmpty ? ' error' : ''}`}>
          <label htmlFor="stepB-manager">현장 관리자</label>
          <div className="flex items-start gap-03">
            <AssigneeSelect
              id="stepB-manager"
              className="min-w-0 flex-1"
              members={companyMembers}
              values={managerIds}
              onChange={setManagerIds}
              myId={myId}
              meFirst
              oppositeIds={chargeIds}
              oppositeLabel="담당자"
              extraNames={myNames}
            />
            {/* 관리자 알림 받기 — 지정한 관리자 전원 일괄(기본 꺼짐) */}
            <span className="flex h-[var(--control-height-base)] shrink-0 items-center gap-03 rounded-md border border-border bg-surface px-04 text-body-sm text-text-secondary">
              <span id="stepB-manager-noti">알림 받기</span>
              <Segmented
                aria-labelledby="stepB-manager-noti"
                size="sm"
                options={[{ label: '켬', value: 'on' }, { label: '끔', value: 'off' }]}
                value={managerNoti}
                onChange={(v) => setManagerNoti(v === 'on' ? 'on' : 'off')}
              />
            </span>
          </div>
          {managerNotiEmpty && <span className="help">관리자 알림 받기를 켬 또는 끔으로 선택해주세요.</span>}
          <span className="text-caption text-text-muted">현장관리자는 현장등록 후 개인별로 알림 받기를 설정할 수 있어요.</span>
        </div>
        </div>
      </Card>

      {/* 메모 */}
      <Input
        id="stepB-memo"
        label="메모 (선택)"
        width="auto"
        type="text"
        placeholder="추가 내용을 입력하세요"
        {...register('siteMemo')}
      />

      {/* 하단 버튼 — [이전]은 주 액션(채움) 옆의 보조 강조라 secondary (D-09 §15.1) */}
      <div className="flex gap-2 pt-1">
        <Button variant="secondary" size="lg" type="button" onClick={onPrev} className="flex-none">
          ← 이전
        </Button>
        <Button
          variant="primary"
          size="lg"
          type="button"
          onClick={() => void handleSubmit(handleFormSubmit)()}
          disabled={submitting || buildingBusy || !watch('siteName') || chargeEmpty || managerNotiEmpty}
          className="flex-1"
        >
          {submitting || buildingBusy ? '처리 중…' : nextLabel}
        </Button>
      </div>
    </div>
  );
}
